import { useEffect, useMemo, useState } from "react";
import { briefQuestions } from "@dropin/shared/schemas";
import type {
  Brief,
  BriefAnswers,
  UpdateBriefInput,
} from "@dropin/shared/schemas";
import {
  adminCreateBusinessBrief,
  adminGetBusinessBrief,
  adminUpdateBusinessBrief,
  createBrief,
  getMyBrief,
  updateMyBrief,
} from "../api";
import { Markdown } from "./Markdown";

// When `businessId` is provided we're an admin editing on behalf of that
// business; otherwise we operate on the caller's own brief.
type BriefApi = {
  get: () => Promise<{ brief: Brief | null }>;
  create: (a: BriefAnswers) => Promise<{ brief: Brief }>;
  update: (p: UpdateBriefInput) => Promise<{ brief: Brief }>;
};

export function BriefTab({ businessId }: { businessId?: string } = {}) {
  const api: BriefApi = useMemo(
    () =>
      businessId
        ? {
            get: () => adminGetBusinessBrief(businessId),
            create: (a) => adminCreateBusinessBrief(businessId, a),
            update: (p) => adminUpdateBusinessBrief(businessId, p),
          }
        : {
            get: getMyBrief,
            create: createBrief,
            update: updateMyBrief,
          },
    [businessId],
  );

  const [brief, setBrief] = useState<Brief | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const { brief } = await api.get();
    setBrief(brief);
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  if (loading) return <div className="card text-muted">Loading…</div>;
  if (!brief) {
    return <BriefWizard api={api} onCreated={(b) => setBrief(b)} />;
  }
  return (
    <>
      {brief.status === "published" && (
        <ShareableLink
          identifier={brief.businessShortCode || brief.businessId}
        />
      )}
      <BriefEditor
        api={api}
        brief={brief}
        onChange={(b) => setBrief(b)}
        onStartOver={() => setBrief(null)}
      />
    </>
  );
}

function ShareableLink({ identifier }: { identifier: string }) {
  const [copied, setCopied] = useState(false);
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/brief/${identifier}`
      : `/brief/${identifier}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Older browsers / non-secure contexts — no-op.
    }
  }

  return (
    <div
      className="rounded-lg border border-success/30 bg-success/[0.05] p-4 flex items-center gap-3 flex-wrap"
      data-testid="shareable-brief-link"
    >
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-success">
          Live & shareable
        </p>
        <p className="mt-1 text-sm font-mono text-ink truncate">{shareUrl}</p>
        <p className="mt-1 text-xs text-muted">
          Share this link with verified creators in your neighborhood.
        </p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={copy}
          className="btn text-xs"
          data-testid="copy-brief-link"
        >
          {copied ? "Copied ✓" : "Copy link"}
        </button>
        <a
          href={shareUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost text-xs"
          data-testid="open-brief-link"
        >
          Open ↗
        </a>
      </div>
    </div>
  );
}

function BriefWizard({
  api,
  onCreated,
}: {
  api: BriefApi;
  onCreated: (b: Brief) => void;
}) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<BriefAnswers>({
    vibe: "",
    highlight: "",
    contentStyle: "",
    noGos: "",
    cta: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = briefQuestions[step]!;
  const value = answers[q.key];
  const isLast = step === briefQuestions.length - 1;

  function setValue(v: string) {
    setAnswers((a) => ({ ...a, [q.key]: v }));
  }

  async function finish() {
    setSaving(true);
    setError(null);
    try {
      const { brief } = await api.create(answers);
      onCreated(brief);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
      setSaving(false);
    }
  }

  return (
    <div className="card" data-testid="brief-wizard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="chip-primary">
          Creator brief · step {step + 1} of {briefQuestions.length}
        </span>
        <div className="text-xs text-muted">
          {briefQuestions.map((_, i) => (
            <span
              key={i}
              className={`inline-block w-1.5 h-1.5 rounded-full mx-0.5 ${
                i === step ? "bg-primary" : i < step ? "bg-ink/60" : "bg-border-strong"
              }`}
            />
          ))}
        </div>
      </div>
      <h3 className="mt-3 text-xl font-semibold tracking-tightish">{q.label}</h3>
      <textarea
        className="input mt-4 min-h-[128px]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={q.placeholder}
        data-testid={`brief-q-${q.key}`}
        autoFocus
      />
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {step > 0 && (
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setStep((s) => s - 1)}
            data-testid="brief-back"
          >
            Back
          </button>
        )}
        <div className="ml-auto">
          {!isLast ? (
            <button
              type="button"
              className="btn-primary"
              disabled={!value.trim()}
              onClick={() => setStep((s) => s + 1)}
              data-testid="brief-next"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={saving || !value.trim()}
              onClick={finish}
              data-testid="brief-generate"
            >
              {saving ? "Building template…" : "Generate template"}
            </button>
          )}
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-rose">{error}</p>}
    </div>
  );
}

function BriefEditor({
  api,
  brief,
  onChange,
  onStartOver,
}: {
  api: BriefApi;
  brief: Brief;
  onChange: (b: Brief) => void;
  onStartOver: () => void;
}) {
  const [content, setContent] = useState(brief.content);
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const { brief: updated } = await api.update({ content });
      onChange(updated);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
    } finally {
      setSaving(false);
    }
  }

  async function togglePublish() {
    setSaving(true);
    setError(null);
    try {
      const next = brief.status === "published" ? "draft" : "published";
      const { brief: updated } = await api.update({ content, status: next });
      onChange(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-3">
      <div className="card flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <span
            className={brief.status === "published" ? "chip-success" : "chip"}
            data-testid="brief-status"
          >
            {brief.status}
          </span>
          {savedAt && (
            <span className="text-xs text-muted" data-testid="brief-saved-at">
              saved {savedAt}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-border bg-paper p-1">
            <button
              className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                mode === "edit" ? "bg-surface text-ink shadow-xs" : "text-muted hover:text-ink"
              }`}
              onClick={() => setMode("edit")}
              data-testid="brief-mode-edit"
            >
              Edit
            </button>
            <button
              className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                mode === "preview" ? "bg-surface text-ink shadow-xs" : "text-muted hover:text-ink"
              }`}
              onClick={() => setMode("preview")}
              data-testid="brief-mode-preview"
            >
              Preview
            </button>
          </div>
          <button
            className="btn-ghost text-xs"
            onClick={onStartOver}
            data-testid="brief-restart"
          >
            Restart wizard
          </button>
        </div>
      </div>

      {mode === "edit" ? (
        <div className="card">
          <p className="text-xs text-muted">
            Markdown: <code className="font-mono">#</code> for headings,{" "}
            <code className="font-mono">**bold**</code>,{" "}
            <code className="font-mono">_italic_</code>,{" "}
            <code className="font-mono">- bullet</code> lists.
          </p>
          <textarea
            className="input mt-3 min-h-[420px] font-mono text-[13px] leading-relaxed"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            data-testid="brief-content"
          />
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              className="btn-primary"
              disabled={saving}
              onClick={save}
              data-testid="brief-save"
            >
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button
              className={brief.status === "published" ? "btn-danger" : "btn-accent"}
              disabled={saving}
              onClick={togglePublish}
              data-testid="brief-publish"
            >
              {brief.status === "published" ? "Unpublish" : "Publish"}
            </button>
          </div>
          {error && <p className="mt-3 text-sm text-rose">{error}</p>}
        </div>
      ) : (
        <div className="card" data-testid="brief-preview">
          <Markdown source={content} />
        </div>
      )}
    </div>
  );
}
